import { useCallback, useEffect } from 'react';
import { useTranslation } from 'next-i18next';
import { getAgeGroup } from '@shared/utils/src/ct/index';
import { Envelope } from '@storefront/lib-global';
import { sendApplyProductVariantEvent } from '@storefront/lib-global/utils/gtm/events/apply_product_variant/sendApplyProductVariantEvent';
import { useSizeAndWidthContext } from '../../../context';
import { useFilteredSizes, useLinePositions } from '../../../hooks';
import { hasBackInStockFlag, hasSizeBackInStockNotificationsStatus, isOutOfStock } from '../../../utils';
import { MeasurementBlock, MeasurementListItem } from '../MeasurementBlock/MeasurementBlock';
import { CrossedLine } from '../SizeSelector/SizeSelector.styles';

export function Sizes() {
  const { handleChangeSize, selectedSize, selectedWidth, uniqueSizes, variants } = useSizeAndWidthContext();
  const { t } = useTranslation(['lib-products-common']);
  const newUniqueSizes = useFilteredSizes();
  const { lineRefs, crossLineRefs, assignLineRef, assignCrossLineRef, updateLinePositions } = useLinePositions();

  const handleClick = (size: string) => () => {
    const updateSize = handleChangeSize(size);
    const sku = updateSize();

    const globalSize = variants.find((variant) => variant.size === size)?.globalSize || '';
    const ageGroup = getAgeGroup(globalSize);

    sendApplyProductVariantEvent({
      variant_name: 'Size',
      variant_value: `${ageGroup ? `${ageGroup.replace(/^\w/, (match) => match.toUpperCase())} size` : 'Size'} ${size}`,
      item_sku: sku as unknown as string,
    });
  };

  const renderNotificationsDisabledCrossedLine = useCallback(
    (
      index: number,
      sizeHasNotificationsDisabled: boolean,
      backInStockNotification: boolean,
      isOOS: boolean,
      currentWidth: string,
    ) => {
      const shouldRenderCrossedLine =
        sizeHasNotificationsDisabled || (!backInStockNotification && isOOS && currentWidth);

      if (!shouldRenderCrossedLine) return null;

      return <CrossedLine ref={assignCrossLineRef(index)} data-testid="notificationsDisabled" className="inverted" />;
    },
    [assignCrossLineRef],
  );

  useEffect(() => {
    const handleResize = () => {
      // updated diagonal line for crossed out size
      updateLinePositions(lineRefs);
      // updated diagonal line for variant with notify me disabled
      updateLinePositions(crossLineRefs, true);
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [selectedSize, selectedWidth, newUniqueSizes, crossLineRefs, lineRefs, updateLinePositions]);

  return (
    <>
      {newUniqueSizes.map((size, index) => {
        const isOOS = isOutOfStock(size, selectedWidth, variants);
        const isSizeUnavailable = isOOS ? 'crossed' : '';
        const isActive = size === selectedSize;
        const backInStockNotification =
          isOOS && selectedWidth ? hasBackInStockFlag(size, selectedWidth, variants) : false;
        const sizeHasNotificationsEnabled = hasSizeBackInStockNotificationsStatus(size, variants, true);
        const sizeHasNotificationsDisabled = hasSizeBackInStockNotificationsStatus(size, variants, false);

        return (
          <MeasurementListItem key={`${size}-${index}`}>
            <MeasurementBlock
              className={isActive || uniqueSizes.length === 1 ? `active ${isSizeUnavailable}` : isSizeUnavailable}
              type="button"
              role="radio"
              aria-label={`${t('size.selector.button.label', { size })}${
                isSizeUnavailable ? `, ${t('size.selector.button.unavailable')}` : ''
              }`}
              aria-checked={isActive || uniqueSizes.length === 1}
              onClick={handleClick(size)}
              title={size}
              data-testid="sizeItem"
            >
              {size}
              {(sizeHasNotificationsEnabled || backInStockNotification) && <Envelope />}
              {isSizeUnavailable && <CrossedLine ref={assignLineRef(index)} data-testid="crossedOut" />}
              {renderNotificationsDisabledCrossedLine(
                index,
                sizeHasNotificationsDisabled,
                backInStockNotification,
                isOOS,
                selectedWidth,
              )}
            </MeasurementBlock>
          </MeasurementListItem>
        );
      })}
    </>
  );
}
